import React, { FC, useCallback, useEffect, useState, useContext, useMemo } from 'react'
import { CrmRolesEnum, IntroducerStatusEnum, KycInternalStatusEnum, RiskScoreEnum } from '@sparling/financial-common'
import { Typography, Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'

import { makeStyles } from '@mui/styles'
import { useParams, useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { TabsComponent } from '../../../components/Tabs'
import { tabList } from './tabList'
import { AppStateContext, useRefresh, useRest } from '../../../components/Context'
import { Loader } from '../../../components/Loader'
import { userPermissionEnum } from '../../../constants/userPermissions'
import { routes } from '../../../constants/routes'

const permissions = [userPermissionEnum.sole_trader, userPermissionEnum.admin, userPermissionEnum.accountant]

export interface IData {
  id: number
  userName: string | null
  firstName: string
  lastName: string
  email: string
  phone: string
  country: string
  city: string
  birthday: string
  role: string
  clientTypeId: string | null
  clientType: string | null
  additionalAssets:
    | Array<{
        id: number
        instrument: string
        label: string
        underlier: string
      }>
    | null
    | undefined
  currency: string
  createdAt: string
  companyName: string
  platform?: string
  companyIdSf?: string
  idSf?: string
  internalKycStatus: KycInternalStatusEnum
  introducerCommission: number
  introducerBankFee: number
  introducerLiquidityFee: number
  introducerStatus: IntroducerStatusEnum
  introducerRejectionReason: string
  introducerApprovedBy: string | null
  introducedBy: {
    idSf: number
    name: string
  } | null
  adminId?: number
  adminIdSf?: string
  commissionFee: number
  riskScore: RiskScoreEnum
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: theme.spacing(6),
  },
  titleWrap: {
    color: theme.palette.primary.light,
  },
  titleId: {
    fontSize: 18,
    lineHeight: 1.25,
    marginTop: 9,
    marginLeft: 10,
  },
  infoWrap: {
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  section: {
    width: '100%',
  },
}))

export const UserProfileTabs: FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const { rxios } = useRest()
  const { saveIsRefresh } = useRefresh()
  const { getIsRefresh, getUserRole } = useContext(AppStateContext)
  const { idSf: clientIdSf, tab } = useParams() as { [key: string]: string }
  const [loading, setLoading] = useState<boolean>(false)
  // eslint-disable-next-line
  const [values, setValues] = useState<any>({})
  const [refresh, setRefresh] = useState<boolean | undefined>()
  const filteredTablist = useMemo(
    () =>
      tabList.filter(
        (t) =>
          (!t.roles || t.roles?.includes(getUserRole?.() as CrmRolesEnum)) &&
          !(
            (values.introducerStatus === IntroducerStatusEnum.NOT_INITIAL && t.tabLabel === 'Introducer') ||
            (t.tabLabel === 'Sparling Accounts' && !permissions.includes(values.role))
          ),
      ),
    [getUserRole, values.introducerStatus, values.role],
  )

  const activeTab = useMemo(() => {
    const currentTab = filteredTablist.find((t) => t.slug === tab)
    return currentTab?.slug || filteredTablist[0]?.slug || ''
  }, [filteredTablist, tab])

  useEffect(() => {
    if (tab !== activeTab && activeTab) {
      history.replace(`${routes.USERS}/${clientIdSf}/${activeTab}`)
    }
  }, [activeTab, tab, history, clientIdSf])

  useEffect(() => setRefresh(getIsRefresh()), [setRefresh, getIsRefresh])

  const loadData = useCallback(async () => {
    let cleanupFunction = false
    setLoading(true)

    rxios.get(`/users/${clientIdSf}`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        setValues(next)
        saveIsRefresh(false)
        if (!cleanupFunction) setLoading(false)
      },
      // eslint-disable-next-line
      (error) => {
        toast.error(error)
        setLoading(false)
      },
      () => {
        setLoading(false)
      },
    )
    cleanupFunction = true
  }, [clientIdSf, rxios, saveIsRefresh])

  useEffect(() => {
    loadData?.()
    // eslint-disable-next-line
  }, [clientIdSf])

  useEffect(() => {
    if (refresh) {
      ;(async () => {
        await loadData()
      })()
    }
    // eslint-disable-next-line
  }, [refresh])

  const title = useCallback(() => {
    if (!values?.email) return null
    return (
      <>
        <Typography variant="h1">
          {activeTab === 'balance' && values.role !== userPermissionEnum.sole_trader
            ? values.companyName
            : `${values.firstName} ${values.lastName}`}
        </Typography>
        <Typography className={classes.titleId}>
          (id:{' '}
          {activeTab === 'balance' && values.role !== userPermissionEnum.sole_trader ? values.companyIdSf : values.idSf}
          )
        </Typography>
      </>
    )
    // eslint-disable-next-line
  }, [values, activeTab])

  const handleTabChange = useCallback(
    (key: string) => {
      history.push(`${routes.USERS}/${clientIdSf}/${key}`)
    },
    [clientIdSf, history],
  )

  return (
    <Grid container className={classes.container}>
      <Grid container className={classes.titleWrap}>
        {title()}
      </Grid>

      {loading ? (
        <Loader />
      ) : (
        <TabsComponent
          data={values}
          tabList={filteredTablist}
          clientId={clientIdSf}
          activeTab={activeTab}
          onChangeTab={handleTabChange}
          refreshProfile={setRefresh}
        />
      )}
    </Grid>
  )
}
